import { AutocompleteRenderInputParams, Stack, TextField, Typography } from "@mui/material";
import { useNewGetUsers, UserDto } from "../http";
import { Autocomplete } from "./Autocomplete";
import { AutocompleteOption } from "./AutocompleteOption";
import React, { useEffect, useRef } from "react";

interface IBaseProps {
  placeholder?: string;
  options?: UserDto[];
  error?: boolean;
  helperText?: React.ReactNode;
  disableClearable?: boolean;
  disabled?: boolean;
  autofocus?: boolean;
  openOnFocus?: boolean;
  optionsFilter?: (user: UserDto) => boolean;
  onBlur?: () => void;
}

interface ISingleProps extends IBaseProps {
  multiple?: false;
  value: UserDto | null;
  onChange?: (value: UserDto | null) => void;
}

interface IMultiProps extends IBaseProps {
  multiple: true;
  value: UserDto[];
  onChange?: (value: UserDto[]) => void;
}

type IProps = ISingleProps | IMultiProps;

const UserAutocomplete = React.forwardRef((props: IProps, ref: React.Ref<unknown>) => {
  const { data, isLoading } = useNewGetUsers(!props.options && !props.disabled);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const isDisabled = isLoading || (data?.users == null && !props.options) || props.disabled;
  const options =
    props.options ?? (data?.users ? (props.optionsFilter ? data.users.filter(props.optionsFilter) : data.users) : []);

  useEffect(() => {
    if (!props.autofocus || isDisabled) {
      return;
    }

    window.setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  }, [props.autofocus, isDisabled]);

  const renderOption = (props: React.HTMLAttributes<HTMLLIElement>, option: UserDto) => (
    <AutocompleteOption {...props} key={option.userId}>
      <Stack>
        <Typography>{option.displayName}</Typography>
        {option.email && <Typography variant="caption">{option.email}</Typography>}
      </Stack>
    </AutocompleteOption>
  );

  const renderInput = (params: AutocompleteRenderInputParams) => (
    <TextField
      {...params}
      inputRef={inputRef}
      placeholder={props.placeholder}
      error={props.error}
      helperText={props.helperText}
      autoFocus={props.autofocus}
    />
  );

  function handleChange(value: UserDto[] | UserDto | null) {
    if (props.multiple) {
      props.onChange?.(Array.isArray(value) ? value : []);
      return;
    }

    props.onChange?.(Array.isArray(value) ? null : value);
  }

  return (
    <Autocomplete
      ref={ref}
      isLoading={isLoading}
      disabled={isDisabled}
      multiple={props.multiple}
      openOnFocus={props.openOnFocus}
      options={options}
      getOptionLabel={(option) => option.displayName}
      onBlur={props.onBlur}
      onChange={(_, value) => handleChange(value)}
      renderOption={renderOption}
      renderInput={renderInput}
      blurOnSelect={false}
      disableClearable={props.disableClearable}
      isOptionEqualToValue={(option, value) => option.userId === value.userId}
      value={props.value}
    />
  );
});

export { UserAutocomplete };
